/**
 * StoryRuntime (spec 020): the story player. It plays a published story
 * manifest scene after scene on one canvas, each scene through spec 012's
 * `SceneRuntime` (one at a time), follows the story graph on the learner's
 * choices, and draws the DOM around the canvas: the choice panel, the
 * "▶ Start" overlay, the loading indicator, the end card and an
 * `aria-live` mirror of the dialog text.
 *
 * Every scene it loads goes through `toPlayableScene`, so a raw scene of a
 * manifest written before spec 020 (`formatVersion` 1) plays. Since
 * Storyboard was retired (spec 006 v5) it plays the stored keyframes only:
 * an unflattened Storyboard scene's entrances and exits play on the
 * `runtime.js` its own package shipped, not on this one.
 *
 * Hosts (the published page, the editor's PlayerHost, spec 009) drive it with
 * `play`, `pause`, `resume`, `seek`, `selectChoice`, `restart`, `goToScene`,
 * `setOrientation`, and read it with `getFrameState` and `getStatus`.
 */

import type {
  ChoiceOption,
  ChoiceSettings,
  FrameState,
  Orientation,
  PlayableScene,
  SceneCompleteEvent,
  StoryCompleteEvent,
  StoryGraphEdge,
  StoryGraphNode,
  StoryPackageManifest,
  StoryPathEntry,
} from '../@types';
import { computeRevealTime, computeSceneAudioCues } from '../utils/sceneTiming';
import type { AudioPort } from './audioCues';
import { createBrowserAudioPort, loadImage as loadBrowserImage, releaseDrawableImage, sceneImageIds, toDrawableImage } from './browserMedia';
import {
  PLAYER_STYLE,
  applyStageFit,
  buildChoicePanel,
  el,
  focusQuietly,
  lockChoicePanel,
  stackChoicePanel,
  stageFit,
} from './playerStage';
import { toPlayableScene, type PlayableSceneInput } from './playableScene';
import { SceneRuntime, type SceneImage, type SceneImages } from './SceneRuntime';
import { SUPPORTED_FORMAT_VERSION } from './version';

/** The choice reveal time (spec 008); defined with the rest of the scene timing. */
export { computeRevealTime };

/** Why the player reports a problem through `onError`; playback goes on where it can. */
export interface StoryRuntimeError {
  code: 'image-load-failed' | 'audio-load-failed' | 'missing-scene' | 'unsupported-format';
  message: string;
  mediaId?: number;
  storySceneId?: number;
}

export interface StorySceneChangeEvent {
  /** The scene as the player plays it. */
  scene: PlayableScene;
  storySceneId: number;
  sceneId: string;
  sceneName: string;
  /** Scenes played in this run, from 0 (restart resets it). */
  index: number;
}

export interface StoryRuntimeStatus {
  storySceneId: number | null;
  sceneId: string | null;
  /** As in `StorySceneChangeEvent`; -1 before the first scene starts. */
  index: number;
  orientation: Orientation;
  /** A scene is mounted and its clock runs. */
  playing: boolean;
  /** The current scene's choice panel is shown. */
  choicesRevealed: boolean;
  /** The "▶ Start" overlay waits for a gesture. */
  waitingForStart: boolean;
  /** The story has ended (end card shown). */
  ended: boolean;
}

export interface StoryRuntimeOptions {
  /** A story manifest; any `formatVersion` up to `SUPPORTED_FORMAT_VERSION`. */
  manifest: StoryPackageManifest;
  /** The player fills it (its own size is the host's). */
  container: HTMLElement;
  /** Where media `id` is served; default `manifest.media[id].filename` resolved against the page URL. */
  resolveMediaUrl?: (mediaId: number) => string;
  /** Force an orientation; default derived from the container (portrait / landscape / desktop). */
  orientation?: Orientation;
  /** Show "▶ Start" before a first scene with sound (browsers need a gesture for audio). Default true. */
  startOverlay?: boolean;
  /** Sound output; default a browser port (`HTMLAudioElement`), `null` = silent. */
  audio?: AudioPort<any> | null;
  /** Load and decode an image; resolves `null` on failure. Default `browserMedia.loadImage`. The runtime keeps it as an `ImageBitmap` where it can. */
  loadImage?: (url: string) => Promise<HTMLImageElement | null>;
  /** Fetch an audio file's metadata before its scene; default an `<audio preload="metadata">` capped at 1.5 s. */
  preloadAudio?: (url: string) => Promise<void>;

  /** The first scene is loaded and drawn (the page posts `ready`). */
  onReady?: () => void;
  /** A scene starts playing. */
  onSceneChange?: (event: StorySceneChangeEvent) => void;
  /** The current scene revealed its choices (options in display order). A host may `pause()` from here. */
  onChoicesReveal?: (choices: ChoiceOption[], settings: ChoiceSettings) => void;
  /** The learner chose (`choice`) or the timer chose the first option (`timeout`). */
  onChoiceMade?: (storySceneId: number, choiceId: string, exitType: 'choice' | 'timeout') => void;
  /** A scene completed (any exit type). */
  onSceneComplete?: (storySceneId: number, event: SceneCompleteEvent) => void;
  /**
   * The story ended. `exitSceneId` is the last played scene's `Scene.id`;
   * `exitChoiceId` the choice that ended it, `null` when it ended on its own;
   * `path` every scene completed in this run, in visit order (spec 015).
   */
  onStoryComplete?: (event: StoryCompleteEvent) => void;
  /** A problem that does not stop the player (or, for `unsupported-format`, that stops it). */
  onError?: (error: StoryRuntimeError) => void;
  /** The current scene's playhead (ms), every frame while playing. */
  onPlayheadUpdate?: (positionMs: number) => void;
}

/** Portrait when taller than wide, landscape when wider and < 1024 px, desktop at 1024 px or more (spec 020). */
export function deriveOrientation(width: number, height: number): Orientation {
  if (!(width > 0) || !(height > 0)) return 'portrait';
  if (height > width) return 'portrait';
  return width >= 1024 ? 'desktop' : 'landscape';
}

/** One stop on the story's path: a graph node, or an index into `sceneOrder`. */
interface Step {
  nodeId: string | null;
  linearIndex: number;
  storySceneId: number;
}

interface Current {
  step: Step;
  scene: PlayableScene;
  runtime: SceneRuntime;
  /** The scene's clock has been started (`SceneRuntime.play`). */
  started: boolean;
  /** `onSceneChange` fired for it. */
  announced: boolean;
}

const LOADING_DELAY_MS = 300;
const AUDIO_PRELOAD_CAP_MS = 1500;

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Default audio metadata preload: resolves on metadata, error, or after the cap. */
function preloadAudioMetadata(url: string): Promise<void> {
  return new Promise((resolve) => {
    if (typeof Audio === 'undefined' || !url) return resolve();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(cap);
      resolve();
    };
    const cap = setTimeout(finish, AUDIO_PRELOAD_CAP_MS);
    try {
      const audio = new Audio();
      audio.preload = 'metadata';
      audio.addEventListener('loadedmetadata', finish, { once: true });
      audio.addEventListener('error', finish, { once: true });
      audio.src = url;
    } catch {
      finish();
    }
  });
}

export class StoryRuntime {
  private readonly manifest: StoryPackageManifest;
  private readonly options: StoryRuntimeOptions;
  private readonly resolveUrl: (mediaId: number) => string;
  private readonly audioPort: AudioPort<any> | undefined;
  private readonly unsupported: boolean;

  private readonly root: HTMLDivElement;
  private readonly stage: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly live: HTMLDivElement;
  /** Announces the choices and the countdown (spec 015); the dialog mirror stays in `live`. */
  private readonly choiceLive: HTMLDivElement;
  private panel: HTMLDivElement | null = null;
  private timerEl: HTMLSpanElement | null = null;
  private loadingEl: HTMLDivElement | null = null;
  private startEl: HTMLDivElement | null = null;
  private endEl: HTMLDivElement | null = null;

  private orientation: Orientation;
  private forcedOrientation: Orientation | null;
  private resizeObserver: ResizeObserver | null = null;
  private readonly onWindowResize = () => this.fit();

  private current: Current | null = null;
  /** Bumped by every navigation: a stale async load or completion is ignored. */
  private nav = 0;
  private index = -1;
  private hostPaused = false;
  private ended = false;
  private destroyed = false;
  private loaded: Promise<void> | null = null;
  private lastScene: PlayableScene | null = null;
  private lastChoiceId: string | null = null;
  private announcedLine = '';
  /** The scenes completed in this run, in visit order (spec 015); `restart()` starts a new one. */
  private path: StoryPathEntry[] = [];
  /** The open panel's options in display order, for the number keys (spec 015). */
  private panelChoices: ChoiceOption[] = [];
  /** A selection was made on the open panel: further keys and clicks do nothing. */
  private choiceLocked = false;
  /** The last countdown second announced to screen readers. */
  private announcedSecond: number | null = null;

  private readonly playable = new Map<string, PlayableScene | null>();
  /** Per media id: the decoded image, as a bitmap where the browser can make one (released on `destroy`). */
  private readonly images = new Map<number, Promise<SceneImage | null>>();
  private readonly audioMeta = new Map<number, Promise<void>>();
  private readonly reported = new Set<string>();

  private countdown: { endsAt: number; remaining: number | null; interval: ReturnType<typeof setInterval> | null } | null = null;
  private loadingTimer: ReturnType<typeof setTimeout> | null = null;
  /** Number keys 1–4 pick the visible option at that position while a panel is open (spec 015). */
  private readonly onPanelKey = (e: KeyboardEvent) => {
    if (!this.panel || e.ctrlKey || e.metaKey || e.altKey) return;
    if (!/^[1-4]$/.test(e.key)) return;
    e.preventDefault();
    const option = this.panelChoices[Number(e.key) - 1];
    if (option) this.choose(option.id);
  };
  private readonly onStartKey = (e: KeyboardEvent) => {
    if (e.key === 'Tab' || e.key === 'Shift') return;
    e.preventDefault();
    this.begin();
  };

  constructor(options: StoryRuntimeOptions) {
    this.options = options;
    this.manifest = options.manifest;
    this.forcedOrientation = options.orientation ?? null;
    this.resolveUrl = options.resolveMediaUrl ?? ((id) => this.defaultMediaUrl(id));
    this.audioPort = options.audio === null
      ? undefined
      : options.audio ?? (typeof Audio !== 'undefined' ? createBrowserAudioPort((id) => this.resolveUrl(id)) : undefined);

    // Focusable (not in the tab order) so a click on the stage keeps keyboard focus inside the player.
    this.root = el('div', 'sm-root', { 'data-storymotion-player': '', tabindex: '-1' });
    const style = el('style', '');
    style.textContent = PLAYER_STYLE;
    this.stage = el('div', 'sm-stage', { 'data-story-stage': '' });
    this.canvas = el('canvas', 'sm-canvas', { 'aria-hidden': 'true' });
    this.live = el('div', 'sm-sr', { 'aria-live': 'polite', 'data-story-live': '' });
    this.choiceLive = el('div', 'sm-sr', { 'aria-live': 'polite', 'data-story-choice-live': '' });
    this.stage.appendChild(this.canvas);
    this.root.append(style, this.stage, this.live, this.choiceLive);
    this.root.addEventListener('keydown', this.onPanelKey);
    options.container.appendChild(this.root);

    this.orientation = this.forcedOrientation ?? this.containerOrientation();
    this.fit();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.fit());
      this.resizeObserver.observe(this.root);
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onWindowResize);
    }

    const format = typeof this.manifest?.formatVersion === 'number' ? this.manifest.formatVersion : 1;
    this.unsupported = format > SUPPORTED_FORMAT_VERSION;
    if (this.unsupported) {
      const msg = el('div', 'sm-message', { 'data-story-message': '', role: 'alert' });
      msg.textContent = 'This content needs a newer player';
      this.root.appendChild(msg);
      this.report('format', {
        code: 'unsupported-format',
        message: `manifest formatVersion ${format} is newer than this player (${SUPPORTED_FORMAT_VERSION})`,
      });
    }
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /** Load and draw the first scene without starting it (fires `onReady`). */
  load(): Promise<void> {
    if (this.unsupported || this.destroyed) return Promise.resolve();
    if (!this.loaded) this.loaded = this.enter(this.firstStep(), false);
    return this.loaded;
  }

  /**
   * Start the story: loads the first scene, then plays it, behind "▶ Start"
   * when it has sound. After the end it restarts; while paused it resumes.
   */
  async play(): Promise<void> {
    if (this.unsupported || this.destroyed) return;
    if (this.ended) return this.restart();
    if (this.current?.started) {
      this.resume();
      return;
    }
    await this.load();
    const cur = this.current;
    if (!cur || cur.started || this.destroyed || this.ended) return;
    if (this.options.startOverlay !== false && this.hasAudio(cur.scene)) this.showStartOverlay();
    else this.begin();
  }

  /**
   * The learner's choice (as clicking its button). Ignored before the reveal
   * and once the open panel has a selection.
   */
  selectChoice(choiceId: string): void {
    if (this.panel) {
      this.choose(choiceId);
      return;
    }
    this.current?.runtime.selectChoice(choiceId);
  }

  /** Pause the scene clock, its audio, the choice timer and countdown. */
  pause(): void {
    this.hostPaused = true;
    this.current?.runtime.pause();
    this.pauseCountdown();
  }

  /** Resume after `pause()` (also starts a mounted scene that never started). */
  resume(): void {
    if (this.destroyed) return;
    this.hostPaused = false;
    const cur = this.current;
    if (!cur || this.ended || this.startEl) return;
    if (!cur.started) {
      this.startCurrent();
      return;
    }
    cur.runtime.resume();
    this.resumeCountdown();
  }

  /** Move the current scene's playhead to `ms` and render that frame (also while paused). */
  seek(ms: number): void {
    const cur = this.current;
    if (!cur) return;
    cur.runtime.seek(ms);
    this.afterFrame();
  }

  /** Play the story again from its first scene. */
  restart(): Promise<void> {
    if (this.unsupported || this.destroyed) return Promise.resolve();
    this.removeEnd();
    this.removeStartOverlay();
    this.ended = false;
    this.hostPaused = false;
    this.index = -1;
    this.lastScene = null;
    this.lastChoiceId = null;
    this.path = [];
    this.loaded = this.enter(this.firstStep(), true);
    return this.loaded;
  }

  /**
   * Jump to the scene with this `storySceneId` and play it from 0 (hosts,
   * tests and previews). Resolves false when the story has no such scene.
   */
  async goToScene(storySceneId: number): Promise<boolean> {
    if (this.unsupported || this.destroyed) return false;
    const step = this.stepFor(storySceneId);
    if (!step) return false;
    this.removeEnd();
    this.removeStartOverlay();
    this.ended = false;
    this.hostPaused = false;
    await this.enter(step, true);
    return this.current?.step.storySceneId === storySceneId;
  }

  /** Force an orientation, or `null` to derive it from the container again. The scene keeps its t. */
  setOrientation(orientation: Orientation | null): void {
    this.forcedOrientation = orientation;
    this.fit();
  }

  /** The current scene's `FrameState` (what was drawn last); `null` before a scene is mounted. */
  getFrameState(): FrameState | null {
    return this.current?.runtime.getFrameState() ?? null;
  }

  getStatus(): StoryRuntimeStatus {
    const cur = this.current;
    return {
      storySceneId: cur?.step.storySceneId ?? null,
      sceneId: cur?.scene.id ?? null,
      index: this.index,
      orientation: this.orientation,
      playing: !!cur && cur.runtime.isPlaying(),
      choicesRevealed: !!this.panel,
      waitingForStart: !!this.startEl,
      ended: this.ended,
    };
  }

  /** The stage canvas (hosts read pixels or screenshots from it). */
  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.nav++;
    this.current?.runtime.dispose();
    this.current = null;
    this.stopCountdown();
    this.clearLoading();
    this.removeStartOverlay();
    this.root.removeEventListener('keydown', this.onPanelKey);
    this.resizeObserver?.disconnect();
    if (typeof window !== 'undefined') window.removeEventListener('resize', this.onWindowResize);
    this.root.remove();
    // Free the bitmaps, including those still being made.
    for (const p of this.images.values()) void p.then(releaseDrawableImage);
    this.images.clear();
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  private hasGraph(): boolean {
    return !!this.manifest.graph && Array.isArray(this.manifest.graph.nodes) && this.manifest.graph.nodes.length > 0;
  }

  private nodeById(nodeId: string): StoryGraphNode | undefined {
    return this.manifest.graph?.nodes.find((n) => n.id === nodeId);
  }

  private stepForNode(nodeId: string): Step {
    const node = this.nodeById(nodeId);
    return { nodeId, linearIndex: -1, storySceneId: node?.storySceneId ?? 0 };
  }

  private linearStep(i: number): Step | null {
    const order = this.manifest.sceneOrder ?? [];
    if (i < 0 || i >= order.length) return null;
    return { nodeId: null, linearIndex: i, storySceneId: Number(order[i]) || 0 };
  }

  private firstStep(): Step | null {
    if (this.hasGraph()) return this.stepForNode(this.manifest.graph!.startNodeId);
    return this.linearStep(0);
  }

  private stepFor(storySceneId: number): Step | null {
    if (this.hasGraph()) {
      const node = this.manifest.graph!.nodes.find((n) => n.type !== 'end' && n.storySceneId === storySceneId);
      return node ? this.stepForNode(node.id) : null;
    }
    const i = (this.manifest.sceneOrder ?? []).indexOf(String(storySceneId));
    return i >= 0 ? this.linearStep(i) : null;
  }

  /** The step a completed scene leads to; `null` ends the story. */
  private nextStep(step: Step, event: SceneCompleteEvent): Step | null {
    if (step.nodeId !== null) {
      const edges: StoryGraphEdge[] = this.manifest.graph?.edges.filter((e) => e.from === step.nodeId) ?? [];
      const edge = event.exitType === 'auto'
        ? edges.find((e) => !e.condition)
        : edges.find((e) => e.condition === event.choiceId);
      return edge ? this.stepForNode(edge.to) : null;
    }
    return this.linearStep(step.linearIndex + 1);
  }

  /** The playable scene of a step, or null (an end node, or a scene missing from the manifest). */
  private sceneFor(step: Step): PlayableScene | null {
    const key = step.nodeId !== null ? `n:${step.nodeId}` : `i:${step.linearIndex}`;
    if (this.playable.has(key)) return this.playable.get(key)!;
    let raw: PlayableSceneInput | undefined;
    const map = this.manifest.sceneMap as Record<string, PlayableSceneInput> | undefined;
    if (step.nodeId !== null) {
      raw = map?.[String(step.storySceneId)];
      if (!raw) {
        const i = (this.manifest.sceneOrder ?? []).indexOf(String(step.storySceneId));
        raw = i >= 0 ? (this.manifest.scenes?.[i] as PlayableSceneInput | undefined) : undefined;
      }
    } else {
      raw = (this.manifest.scenes?.[step.linearIndex] as PlayableSceneInput | undefined)
        ?? map?.[String(step.storySceneId)];
    }
    const scene = raw ? toPlayableScene(raw) : null;
    this.playable.set(key, scene);
    return scene;
  }

  private isEndNode(step: Step): boolean {
    return step.nodeId !== null && this.nodeById(step.nodeId)?.type === 'end';
  }

  /**
   * Go to a step: preload its media (indicator after 300 ms), mount its
   * `SceneRuntime` on the canvas (disposing the previous one first) and, when
   * `start`, play it. An end node, no step or a missing scene ends the story.
   */
  private async enter(step: Step | null, start: boolean): Promise<void> {
    const token = ++this.nav;
    this.removePanel();
    // Only one scene ever runs: the one being left stops now (its last frame stays on the canvas).
    this.current?.runtime.stop();
    if (!step || this.isEndNode(step)) {
      this.finish();
      return;
    }
    const scene = this.sceneFor(step);
    if (!scene) {
      this.report(`scene:${step.nodeId ?? step.linearIndex}`, {
        code: 'missing-scene',
        message: `story scene ${step.storySceneId}${step.nodeId ? ` (node ${step.nodeId})` : ''} is not in the manifest`,
        storySceneId: step.storySceneId,
      });
      this.finish();
      return;
    }

    this.clearLoading();
    this.loadingTimer = setTimeout(() => {
      this.loadingTimer = null;
      if (token !== this.nav || this.destroyed) return;
      this.loadingEl = el('div', 'sm-loading', { 'data-story-loading': '', role: 'status' });
      this.loadingEl.textContent = 'Loading…';
      this.stage.appendChild(this.loadingEl);
    }, LOADING_DELAY_MS);
    const images = await this.preload(scene, true);
    if (token !== this.nav || this.destroyed) return;
    this.clearLoading();

    this.current?.runtime.dispose();
    const runtime = new SceneRuntime({
      scene,
      canvas: this.canvas,
      orientation: this.orientation,
      images,
      audio: this.audioPort,
      playbackSettings: this.manifest.playbackSettings,
      onComplete: (e) => {
        if (token === this.nav) this.onSceneComplete(e);
      },
      onChoicesReveal: (choices, settings) => {
        if (token === this.nav) this.showChoices(choices, settings);
      },
      onPlayheadUpdate: (ms) => {
        if (token !== this.nav) return;
        this.afterFrame();
        this.options.onPlayheadUpdate?.(ms);
      },
    });
    this.current = { step, scene, runtime, started: false, announced: false };
    this.lastScene = scene;
    this.announcedLine = '';
    runtime.seek(0);
    this.afterFrame();

    if (start) {
      this.startCurrent();
    } else {
      this.options.onReady?.();
    }
    this.prefetchNext(step);
  }

  /** Start the mounted scene (unless the host paused) and announce it once. */
  private startCurrent(): void {
    const cur = this.current;
    if (!cur) return;
    if (!cur.announced) {
      cur.announced = true;
      this.index += 1;
      this.options.onSceneChange?.({
        scene: cur.scene,
        storySceneId: cur.step.storySceneId,
        sceneId: cur.scene.id,
        sceneName: cur.scene.name,
        index: this.index,
      });
    }
    if (this.hostPaused || cur.started || this.current !== cur) return;
    cur.started = true;
    cur.runtime.play(cur.runtime.getPlayheadPosition());
  }

  /** The first gesture: leave the Start overlay and play. */
  private begin(): void {
    if (this.destroyed) return;
    this.removeStartOverlay();
    this.startCurrent();
  }

  private onSceneComplete(event: SceneCompleteEvent): void {
    const cur = this.current;
    if (!cur) return;
    this.removePanel();
    this.path.push({
      storySceneId: cur.step.storySceneId,
      exitType: event.exitType,
      choiceId: event.exitType === 'auto' ? null : event.choiceId,
    });
    if (event.exitType !== 'auto' && event.choiceId !== null) {
      this.lastChoiceId = event.choiceId;
      this.options.onChoiceMade?.(cur.step.storySceneId, event.choiceId, event.exitType);
    } else {
      this.lastChoiceId = null;
    }
    this.options.onSceneComplete?.(cur.step.storySceneId, event);
    void this.enter(this.nextStep(cur.step, event), true);
  }

  /** The story ended: the last frame holds dimmed under the end card. */
  private finish(): void {
    this.removePanel();
    this.clearLoading();
    this.ended = true;
    this.showEnd();
    this.options.onStoryComplete?.({
      exitSceneId: this.lastScene?.id ?? '',
      exitChoiceId: this.lastChoiceId,
      path: this.path.map((entry) => ({ ...entry })),
    });
  }

  // ── Media ─────────────────────────────────────────────────────────────────

  private defaultMediaUrl(mediaId: number): string {
    const entry = this.manifest.media?.[mediaId];
    if (!entry?.filename) return '';
    try {
      const base = typeof document !== 'undefined' ? document.baseURI : undefined;
      return base ? new URL(entry.filename, base).href : entry.filename;
    } catch {
      return entry.filename;
    }
  }

  private image(mediaId: number): Promise<SceneImage | null> {
    let p = this.images.get(mediaId);
    if (!p) {
      const url = this.resolveUrl(mediaId);
      const loader = this.options.loadImage ?? loadBrowserImage;
      p = (url ? loader(url).catch(() => null) : Promise.resolve(null)).then((img) => {
        if (!img) {
          this.report(`image:${mediaId}`, {
            code: 'image-load-failed',
            message: `image ${mediaId} failed to load${url ? ` (${url})` : ' (not in the manifest)'}`,
            mediaId,
          });
          return null;
        }
        return toDrawableImage(img);
      });
      this.images.set(mediaId, p);
    }
    return p;
  }

  private audioIds(scene: PlayableScene): number[] {
    const ids = new Set<number>();
    for (const cue of computeSceneAudioCues(scene, this.manifest.playbackSettings, scene.duration)) {
      if (cue.mediaId > 0) ids.add(cue.mediaId);
    }
    return Array.from(ids);
  }

  private hasAudio(scene: PlayableScene): boolean {
    return this.audioIds(scene).length > 0;
  }

  /** Decode the scene's images (and, when `withAudio`, fetch its audio metadata). */
  private async preload(scene: PlayableScene, withAudio: boolean): Promise<SceneImages> {
    const ids = sceneImageIds(scene);
    const audio = withAudio && this.audioPort
      ? this.audioIds(scene).map((id) => {
        let p = this.audioMeta.get(id);
        if (!p) {
          const preload = this.options.preloadAudio ?? preloadAudioMetadata;
          p = preload(this.resolveUrl(id)).catch(() => undefined);
          this.audioMeta.set(id, p);
        }
        return p;
      })
      : [];
    const decoded = await Promise.all(ids.map((id) => this.image(id)));
    await Promise.all(audio);
    const images: SceneImages = {};
    ids.forEach((id, i) => {
      const img = decoded[i];
      if (img) images[id] = img;
    });
    return images;
  }

  /** Warm the images of every scene one step away. */
  private prefetchNext(step: Step): void {
    const next: Step[] = [];
    if (step.nodeId !== null) {
      for (const e of this.manifest.graph?.edges ?? []) if (e.from === step.nodeId) next.push(this.stepForNode(e.to));
    } else {
      const s = this.linearStep(step.linearIndex + 1);
      if (s) next.push(s);
    }
    for (const s of next) {
      if (this.isEndNode(s)) continue;
      const target = this.sceneFor(s);
      if (target) void this.preload(target, false);
    }
  }

  private report(key: string, error: StoryRuntimeError): void {
    if (this.reported.has(key)) return;
    this.reported.add(key);
    if (this.options.onError) this.options.onError(error);
    else console.warn(`[StoryMotion] ${error.message}`);
  }

  // ── Stage and orientation ─────────────────────────────────────────────────

  private containerSize(): { width: number; height: number } {
    return { width: this.root.clientWidth, height: this.root.clientHeight };
  }

  private containerOrientation(): Orientation {
    const { width, height } = this.containerSize();
    return deriveOrientation(width, height);
  }

  /** Re-derive the orientation and scale the stage to fit the container, centred (letterboxed on black). */
  private fit(): void {
    if (this.destroyed) return;
    const orientation = this.forcedOrientation ?? this.containerOrientation();
    if (orientation !== this.orientation) {
      this.orientation = orientation;
      this.current?.runtime.setOrientation(orientation);
      this.afterFrame();
    }
    const { width: cw, height: ch } = this.containerSize();
    // A changed pixel ratio (zoom, another screen) resizes the buffer, which blanks it: redraw.
    if (applyStageFit(this.stage, this.canvas, this.orientation, stageFit(this.orientation, cw, ch))) {
      this.current?.runtime.setOrientation(this.orientation);
    }
  }

  /** After each drawn frame: the choice panel's stacking and the aria-live dialog text. */
  private afterFrame(): void {
    const frame = this.current?.runtime.getFrameState();
    if (!frame) return;
    // A timeout started the scene's exit: the panel locks as for a selection (spec 015).
    if (this.panel && !this.choiceLocked && this.current!.runtime.isExiting()) this.lockPanel();
    if (this.panel) stackChoicePanel(this.panel, this.current!.runtime.getDialogBoxes(), frame.stage.height);
    const scene = this.current!.scene;
    const shown = frame.dialogs.find((d) => d.lineId && d.panelOpacity > 0);
    const key = shown ? `${shown.id}:${shown.lineId}` : '';
    if (key !== this.announcedLine) {
      this.announcedLine = key;
      const line = shown ? scene.dialogs.find((d) => d.id === shown.id)?.lines.find((l) => l.id === shown.lineId) : undefined;
      this.live.textContent = line?.text ?? '';
    }
  }

  // ── Choice panel ──────────────────────────────────────────────────────────

  private showChoices(choices: ChoiceOption[], settings: ChoiceSettings): void {
    this.removePanel();
    const { panel, buttons, timerEl, seconds } = buildChoicePanel(choices, settings, (id) => this.choose(id));
    this.panel = panel;
    this.panelChoices = choices;
    this.choiceLocked = false;
    this.timerEl = timerEl;
    this.stage.appendChild(panel);
    this.afterFrame();
    this.announcedSecond = seconds > 0 ? Math.ceil(seconds) : null;
    this.choiceLive.textContent = `Choose an option: ${choices.map((c) => c.text).join(', ')}`;
    if (seconds > 0) this.startCountdown(seconds * 1000);
    focusQuietly(buttons[0]);
    this.options.onChoicesReveal?.(choices, settings);
  }

  /**
   * One selection per panel: the first locks it (buttons disabled), later keys
   * and clicks do nothing. The panel stays, locked, while the scene's exit
   * transition plays (spec 015); it goes when the scene completes.
   */
  private choose(choiceId: string): void {
    const cur = this.current;
    if (!this.panel || this.choiceLocked || !cur || !cur.runtime.isChoiceRevealed()) return;
    if (!this.panelChoices.some((c) => c.id === choiceId)) {
      cur.runtime.selectChoice(choiceId); // warns about the unknown id
      return;
    }
    this.lockPanel();
    cur.runtime.selectChoice(choiceId);
  }

  /** Disable the open panel's buttons; the countdown stops with the choice timer. */
  private lockPanel(): void {
    if (!this.panel) return;
    this.choiceLocked = true;
    this.stopCountdown();
    lockChoicePanel(this.panel);
  }

  private removePanel(): void {
    this.stopCountdown();
    this.panel?.remove();
    this.panel = null;
    this.panelChoices = [];
    this.timerEl = null;
    this.announcedSecond = null;
    this.choiceLive.textContent = '';
  }

  private startCountdown(ms: number): void {
    this.stopCountdown();
    this.countdown = { endsAt: now() + ms, remaining: null, interval: null };
    this.countdown.interval = setInterval(() => this.renderCountdown(), 200);
    this.renderCountdown();
    if (this.hostPaused) this.pauseCountdown();
  }

  private renderCountdown(): void {
    if (!this.countdown || !this.timerEl) return;
    const left = this.countdown.remaining ?? this.countdown.endsAt - now();
    const seconds = Math.max(0, Math.ceil(left / 1000));
    this.timerEl.textContent = String(seconds);
    // Screen readers hear the time left every 5 s and at 3 s (spec 015).
    if (seconds > 0 && seconds !== this.announcedSecond && (seconds % 5 === 0 || seconds === 3)
        && (this.announcedSecond === null || seconds < this.announcedSecond)) {
      this.announcedSecond = seconds;
      this.choiceLive.textContent = `${seconds} seconds left`;
    }
  }

  private pauseCountdown(): void {
    const c = this.countdown;
    if (!c || c.remaining !== null) return;
    c.remaining = Math.max(0, c.endsAt - now());
    if (c.interval !== null) clearInterval(c.interval);
    c.interval = null;
  }

  private resumeCountdown(): void {
    const c = this.countdown;
    if (!c || c.remaining === null) return;
    c.endsAt = now() + c.remaining;
    c.remaining = null;
    c.interval = setInterval(() => this.renderCountdown(), 200);
    this.renderCountdown();
  }

  private stopCountdown(): void {
    if (this.countdown?.interval != null) clearInterval(this.countdown.interval);
    this.countdown = null;
  }

  // ── Overlays ──────────────────────────────────────────────────────────────

  private clearLoading(): void {
    if (this.loadingTimer !== null) clearTimeout(this.loadingTimer);
    this.loadingTimer = null;
    this.loadingEl?.remove();
    this.loadingEl = null;
  }

  private showStartOverlay(): void {
    if (this.startEl) return;
    const overlay = el('div', 'sm-start', { 'data-story-start': '' });
    const button = el('button', 'sm-button', { type: 'button', 'aria-label': 'Start' });
    button.textContent = '▶ Start';
    overlay.appendChild(button);
    overlay.addEventListener('click', () => this.begin());
    this.startEl = overlay;
    this.root.appendChild(overlay);
    if (typeof document !== 'undefined') document.addEventListener('keydown', this.onStartKey);
    try {
      button.focus({ preventScroll: true });
    } catch {
      button.focus();
    }
  }

  private removeStartOverlay(): void {
    if (typeof document !== 'undefined') document.removeEventListener('keydown', this.onStartKey);
    this.startEl?.remove();
    this.startEl = null;
  }

  private showEnd(): void {
    this.removeEnd();
    const end = el('div', 'sm-end', { 'data-story-end': '' });
    const label = el('p', '');
    label.textContent = 'The end';
    const replay = el('button', 'sm-button', { type: 'button', 'data-story-replay': '' });
    replay.textContent = 'Replay';
    replay.addEventListener('click', () => void this.restart());
    end.append(label, replay);
    this.endEl = end;
    this.stage.appendChild(end);
    try {
      replay.focus({ preventScroll: true });
    } catch {
      replay.focus();
    }
  }

  private removeEnd(): void {
    this.endEl?.remove();
    this.endEl = null;
  }
}

export default StoryRuntime;
